/**
 * iOS 26's press feedback on Liquid Glass: while a finger is on a bar button
 * or a glass button, the glass under it swells (scales up a little) and
 * brightens; when the finger lifts it springs back. The stylesheet draws it
 * (`[data-sb-pressed]`, `[data-sb-pressed-item]` in styles.css); this
 * controller marks the glass under the pressed control from the pointer
 * down to the pointer up, as UIKit highlights a button while it tracks a
 * touch: lifting the highlight when the pointer leaves the control (plus a
 * margin) and restoring it when it comes back.
 *
 * The glass under a control: a bar item's capsule (the `.sb-bar-glass`
 * under it: the glass is beside the toolbars drawn on it, not around them,
 * so they swell along, about the capsule's center), the back and More
 * circles, the sidebar toggle, a `.glass` button, or the `.glassEffect` box
 * around a button. iOS swells the whole group, never one item of it: a
 * glass-styled button in a bar group is the group's press. A control on no
 * glass (a plain or bordered button) keeps the stylesheet's `:active` dimming.
 */

/** The controls a press can highlight the glass under. */
const PRESSABLE = '.sb-button, .sb-menu, .sb-navbar-back, .sb-navbar-more, .sb-navsplit-toggle, .sb-picker[data-sb-style="menu"]';

/** Controls that are their own glass. */
const OWN_GLASS = '.sb-navbar-back, .sb-navbar-more, .sb-navsplit-toggle, .sb-button[data-sb-style="glass"]';

/** UIKit keeps a button highlighted while the touch stays within this far outside it. */
export const PRESS_SLOP = 24;

/** How long the glass takes to spring back once released (styles.css). */
const SPRING_BACK_MS = 500;

interface Press {
  pointerId: number;
  control: HTMLElement;
  surface: HTMLElement;
  /** The toolbars drawn on the capsule, which swell with it, and their `transform-origin` before. */
  along: { el: HTMLElement; origin: string }[];
  /** Where the control was on the pointer down (its bounds plus the slop hold the press). */
  bounds: DOMRect;
  held: boolean;
}

export class PressEffects {
  private press: Press | null = null;

  constructor(private readonly root: HTMLElement) {
    root.addEventListener('pointerdown', this.onPointerDown, true);
  }

  dispose(): void {
    this.root.removeEventListener('pointerdown', this.onPointerDown, true);
    this.release();
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.press || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const control = (event.target as Element | null)?.closest<HTMLElement>(PRESSABLE);
    if (!control || !this.root.contains(control) || isDisabled(control)) return;
    const surface = glassSurfaceOf(control, this.root);
    if (!surface) return;
    this.press = { pointerId: event.pointerId, control, surface, along: toolbarsOn(surface), bounds: control.getBoundingClientRect(), held: false };
    this.hold(true);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const press = this.press;
    if (!press || event.pointerId !== press.pointerId) return;
    const b = press.bounds;
    const inside =
      event.clientX >= b.left - PRESS_SLOP &&
      event.clientX <= b.right + PRESS_SLOP &&
      event.clientY >= b.top - PRESS_SLOP &&
      event.clientY <= b.bottom + PRESS_SLOP;
    this.hold(inside);
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.press && event.pointerId === this.press.pointerId) this.release();
  };

  /** Marks (or unmarks) the pressed glass and the control on it. */
  private hold(held: boolean): void {
    const press = this.press;
    if (!press || press.held === held) return;
    press.held = held;
    press.surface.toggleAttribute('data-sb-pressed', held);
    if (press.control !== press.surface) press.control.toggleAttribute('data-sb-pressed-item', held);
    for (const { el } of press.along) el.toggleAttribute('data-sb-pressed-with', held);
  }

  private release(): void {
    this.hold(false);
    const press = this.press;
    this.press = null;
    if (press) {
      // The origins stay until the spring back has run; the next press sets them again.
      const settle = (): void => {
        if (this.press?.surface === press.surface) return;
        for (const { el, origin } of press.along) el.style.transformOrigin = origin;
      };
      window.setTimeout(settle, SPRING_BACK_MS);
    }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }
}

/** A control the renderer disabled (`.disabled(true)` marks the element around it). */
function isDisabled(control: HTMLElement): boolean {
  return control.closest('[data-sb-disabled]') !== null || control.getAttribute('aria-disabled') === 'true';
}

/**
 * The Liquid Glass under `control`, or null when it sits on none (see the
 * top of the file).
 */
export function glassSurfaceOf(control: HTMLElement, root: HTMLElement): HTMLElement | null {
  const toolbar = control.closest<HTMLElement>('.sb-toolbar');
  if (toolbar && root.contains(toolbar)) {
    const placement = toolbar.dataset['sbPlacement'];
    if (placement === 'leading' || placement === 'trailing' || placement === 'bottom') return barGlassUnder(control, toolbar);
    return null;
  }
  if (control.matches(OWN_GLASS)) return control;
  const glass = control.closest<HTMLElement>('[data-sb-glass]');
  return glass && root.contains(glass) ? glass : null;
}

/**
 * The toolbars drawn on a capsule (`data-sb-toolbars`, src/layoutDom.ts),
 * each given the capsule's center as its `transform-origin` so that it
 * swells with the capsule rather than about itself.
 */
function toolbarsOn(surface: HTMLElement): { el: HTMLElement; origin: string }[] {
  const ids = surface.dataset['sbToolbars'];
  const screen = surface.parentElement;
  if (ids === undefined || !screen) return [];
  const g = surface.getBoundingClientRect();
  const cx = g.left + g.width / 2;
  const cy = g.top + g.height / 2;
  const along: { el: HTMLElement; origin: string }[] = [];
  for (const id of ids.split(' ')) {
    const el = id ? screen.querySelector<HTMLElement>(`.sb-toolbar[data-sb-id="${id}"]`) : null;
    if (!el) continue;
    const r = el.getBoundingClientRect();
    along.push({ el, origin: el.style.transformOrigin });
    el.style.transformOrigin = `${cx - r.left}px ${cy - r.top}px`;
  }
  return along;
}

/** The capsule a bar item is drawn on: the screen's `.sb-bar-glass` under its center. */
function barGlassUnder(control: HTMLElement, toolbar: HTMLElement): HTMLElement | null {
  const screen = toolbar.closest<HTMLElement>('.sb-navscreen');
  if (!screen) return null;
  const rect = control.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  for (const glass of screen.querySelectorAll<HTMLElement>(':scope > .sb-bar-glass')) {
    const g = glass.getBoundingClientRect();
    if (cx >= g.left && cx <= g.right && cy >= g.top && cy <= g.bottom) return glass;
  }
  return null;
}
